import { useQuery } from '@tanstack/react-query';

import axios from 'src/lib/axios';
import { type QueryConfig } from 'src/lib/react-query.ts';
import { formatResourcePath } from 'src/utils/regex';

import type { DigitalReceipt } from 'src/features/statistics/types/digitalReceipt.ts';

const RESOURCE = '/parkings/:parkingId/fiscal-notes/:fiscalNoteId/';

interface DTO {
    parkingId: string;
    fiscalNoteId: string;
}

type QueryFnType = typeof queryFn;
const queryFn = ({ parkingId, fiscalNoteId }: DTO): Promise<DigitalReceipt> => {
    return axios.get(formatResourcePath(RESOURCE, { parkingId, fiscalNoteId }));
};

interface UseQueryHookOptions extends DTO {
    config?: QueryConfig<QueryFnType>;
}
const useQueryHook = ({ config, ...payload }: UseQueryHookOptions) =>
    useQuery({
        queryKey: [RESOURCE, payload],
        queryFn: async () => {
            const fiscalNote = await queryFn(payload);
            const backupExitDatetime =
                fiscalNote?.enter_datetime && fiscalNote.duration
                    ? new Date(
                          new Date(fiscalNote?.enter_datetime).getTime() + fiscalNote?.duration * 1000,
                      ).toISOString()
                    : null;
            fiscalNote.exit_datetime = fiscalNote?.exit_datetime || backupExitDatetime;

            fiscalNote.duration_hours = fiscalNote?.duration ? Math.floor(fiscalNote?.duration / 3600) : null;
            fiscalNote.duration_minutes = fiscalNote?.duration ? Math.floor((fiscalNote?.duration % 3600) / 60) : null;

            // Sometimes the returned VAT is largely incorrect, so we need to fix it
            if (fiscalNote.tax_percent > 100) {
                fiscalNote.tax_percent /= 10000;
            } else if (fiscalNote.tax_percent > 1) {
                fiscalNote.tax_percent /= 100;
            }

            return fiscalNote;
        },
        ...config,
    });

export { useQueryHook as useGetDigitalReceipt };
